/**
 * Language Profile Registry
 *
 * Central registry for all supported language pairs.
 * Each pair definition generates two directional profiles.
 */

import type { LanguageProfile, LanguagePairDefinition } from './types'
import { enZh, zhEn, enZhPair } from './profiles/en-zh'
import { enZhHant, zhHantEn, enZhHantPair } from './profiles/en-zh-hant'
import { enEs, esEn, enEsPair } from './profiles/en-es'
import { enFr, frEn, enFrPair } from './profiles/en-fr'
import { enDe, deEn, enDePair } from './profiles/en-de'
import { enJa, jaEn, enJaPair } from './profiles/en-ja'
import { enKo, koEn, enKoPair } from './profiles/en-ko'

const PROFILES: Record<string, LanguageProfile> = {
  'en-zh': enZh,
  'zh-en': zhEn,
  'en-zh-hant': enZhHant,
  'zh-hant-en': zhHantEn,
  'en-es': enEs,
  'es-en': esEn,
  'en-fr': enFr,
  'fr-en': frEn,
  'en-de': enDe,
  'de-en': deEn,
  'en-ja': enJa,
  'ja-en': jaEn,
  'en-ko': enKo,
  'ko-en': koEn,
}

const PAIR_DEFINITIONS: LanguagePairDefinition[] = [
  enZhPair,
  enZhHantPair,
  enEsPair,
  enFrPair,
  enDePair,
  enJaPair,
  enKoPair,
]

export function getProfile(id: string): LanguageProfile {
  // Display-path fallback (resilience for corrupt localStorage on the client).
  // Write paths validate via isValidLanguagePair before persisting — never rely
  // on this fallback for a value that gets stored.
  return PROFILES[id] ?? PROFILES['en-zh']
}

/** Trust-boundary check: is `id` a real directional profile id? */
export function isValidLanguagePair(id: string): boolean {
  return id in PROFILES
}

export function getAllProfiles(): LanguageProfile[] {
  return Object.values(PROFILES)
}

export function getPairDefinitions(): LanguagePairDefinition[] {
  return PAIR_DEFINITIONS
}
